import inquiryActions from '../actions/inquiryModalActions';
import contactOwnerFormActions from '../actions/contactOwnerFormActions';
import edapActions from '../actions/edapActions';
import listingExtendedActions from '../actions/listingExtendedActions';
import {postInquiry} from '../api/inquiry';
import {log} from '../api/logger';
import {contactOwnerFormValidateForm} from '../middleware/contactOwnerForm';
import {writeCookie, userAllowsFunctionalCookies, TRIP_COOKIE} from '../utils/cookieHelpers';
import {writeLocalStorage} from '../utils/localStorage';
import {postApplink} from '../api/applink';
import {requestToBook} from './utils/requestToBook';

function inquiryModalRequestToBook(store) {
    return requestToBook(store, 'inquiry modal');
}

/**
 * Builds the inquiry model needed by inquiry-broker
 * Based off of GT's InquiryBean
 * https://puu.sh/vCa6P/ec0d8e55c2.png
 *
 * @param {*} listing - listingReducer data
 * @param {*} tripData - tripDetailsReducer data
 * @param {*} system - systemReducer data
 * @param {*} inquiryFormData - contactOwnerFormReducer data
 */
function buildInquiryModel(listing, tripData, system, inquiryFormData) {
    // Explicitly send an ISO (UTC) string of the date if it is set.
    const arrival = tripData.arriveDateText ? new Date(tripData.arriveDateText).toISOString() : '';
    const departure = tripData.departDateText
        ? new Date(tripData.departDateText).toISOString()
        : '';

    return {
        // listing - need to send this to backend?
        spu: listing.listingId,
        firstName: inquiryFormData.firstName,
        lastName: inquiryFormData.lastName,
        email: inquiryFormData.emailAddress,
        phoneNumber: inquiryFormData.phoneNumber,
        phoneCountryCode: inquiryFormData.countryCode,
        comments: inquiryFormData.message,
        arrival,
        departure,
        ArrivalDate: tripData.arriveDateText,
        DepartureDate: tripData.departDateText,
        numberOfAdults: tripData.adults,
        numberOfChildren: tripData.children,
        pets: !!tripData.hasPets,
        // brand-specific terms agreement; on HAUS clicking submit implies agreement, others have checkbox
        termsAndConditionsAgreed: inquiryFormData.termsAndConditionsAgreed,
        brandId: +system.brandId,
        ipAddress: '0.0.0.0'

        // propertyURL: listing.detailPageUrl,
        // propertyId: listing.propertyId,
        // inquirerPhoneCountry: 'USA', // TODO: get countries on country code slot of inquiry form
    };
}

function inquiryUpdateStorage(tripData, inquiryFormData) {
    writeCookie(TRIP_COOKIE, {
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        numberOfAdults: tripData.adults,
        numberOfChildren: tripData.children,
        petIncluded: tripData.hasPets
    });

    if (userAllowsFunctionalCookies()) {
        // write to local storage
        writeLocalStorage('ha-inquiry-prst', {
            firstName: inquiryFormData.firstName,
            lastName: inquiryFormData.lastName,
            emailAddress: inquiryFormData.emailAddress,
            phoneNumber: inquiryFormData.phoneNumber,
            inquirerPhoneCountry: 'USA',
            countryCode: inquiryFormData.countryCode,
            countryName: inquiryFormData.countryName
        });
    }
}

/**
 * Builds inquiry model and calls API to submit an inquiry
 *
 * @param {} store
 * @param {*} action
 */

function inquiryPostSubmission(store, listing, inquiryModel) {
    // Logging inquiryPostSubmission and postInquiry to ensure there is parity between
    // it and inquiryFailure.
    log({
        listingId: listing.listingId,
        inquiryPostSubmission: true
    });

    const state = store.getState();
    const {siteName, locale, useMockData} = state.systemReducer;
    store.dispatch(inquiryActions.inquiryPosting());
    return postInquiry(inquiryModel, siteName, locale, useMockData)
        .then((response) => {
            if (response.statusCode >= 400) {
                log({
                    postInquiry: false,
                    statusCode: response.statusCode,
                    error: 'Error statusCode'
                });
                store.dispatch(
                    inquiryActions.inquiryError({
                        error: 'Internal Error. Please try again.'
                    })
                );
                store.dispatch(contactOwnerFormActions.scrollTop(true));
                return;
            } else if (!response.body) {
                log({
                    postInquiry: false,
                    error: 'Missing Response body',
                    statusCode: response.statusCode
                });
                store.dispatch(
                    inquiryActions.inquiryError({
                        error: 'Internal Error. Please try again.'
                    })
                );
                store.dispatch(contactOwnerFormActions.scrollTop(true));
                return;
            }

            const inquiryId = response.body.inquiryId;
            const eventRequestMarker = response.headers['x-homeaway-requestmarker'];

            store.dispatch(inquiryActions.inquirySuccess(inquiryId));
            store.dispatch(listingExtendedActions.fetchInquiryProperties());
            store.dispatch(inquiryActions.inquiryApplinkUpdate());
            log({
                postInquiry: true,
                inquiryId,
                eventRequestMarker
            });
            try {
                store.dispatch(
                    edapActions.triggerEdapEvent('inquiry.success', {
                        eventlistingids: listing.spu,
                        inquiryid: inquiryId,
                        eventrequestmarker: eventRequestMarker
                    })
                );
            } catch (error) {
                log({
                    dispatchInquirySuccessError: true,
                    message: error.message,
                    stack: error.stack
                });
            }
        })
        .catch((error) => {
            // Being cautious here...
            let errorMessage = 'postInquiry exception';
            if (error.message) {
                // This is an Error object.
                errorMessage += ` message=${error.message}`;
            }
            if (error.stack) {
                // This is an Error object.
                errorMessage += ` stack=${error.stack}`;
            }

            const errorObj = {error: 'Generic error'};

            // Remove PII from payload
            const loggableInquiryData = {...inquiryModel};
            delete loggableInquiryData.firstName;
            delete loggableInquiryData.lastName;
            delete loggableInquiryData.email;
            delete loggableInquiryData.phoneNumber;

            log({
                postInquiry: false,
                inquiryModel: JSON.stringify(loggableInquiryData),
                errorMessage
            });
            store.dispatch(inquiryActions.inquiryError(errorObj));
            store.dispatch(contactOwnerFormActions.scrollTop(true));
        });
}

/**
 * Validates form and submits the data
 *
 * @param {} store
 * @param {*} action
 */
function inquirySubmit(store) {
    const state = store.getState();
    const system = state.systemReducer;
    const listing = state.listingReducer;
    const tripData = state.tripDetailsReducer;
    const inquiryFormData = state.contactOwnerFormReducer;
    // call buildInquiryModel
    const inquiryModel = buildInquiryModel(listing, tripData, system, inquiryFormData);

    // validate form
    const formIsValid = contactOwnerFormValidateForm(store);

    // if everything was valid, call postInquiry
    if (formIsValid) {
        store.dispatch(
            edapActions.triggerEdapEvent('inquiry.attempt', {eventlistingids: listing.spu})
        );
        store.dispatch(contactOwnerFormActions.contactOwnerFormValid(true));

        inquiryUpdateStorage(tripData, inquiryFormData);
        inquiryPostSubmission(store, listing, inquiryModel);
    } else {
        store.dispatch(contactOwnerFormActions.contactOwnerFormValid(false));
    }
}

function inquiryPostApplink(store) {
    const state = store.getState();
    const system = state.systemReducer;
    return postApplink(system.travelerAppBrand)
        .then((response) => {
            if (response && response.body && response.body.mobileLink) {
                store.dispatch(inquiryActions.inquiryApplinkSuccess(response.body.mobileLink));
            }
        })
        .catch((error) => {
            log({
                error: `postApplink exception.  Error: ${error}`
            });
        });
}

export default inquirySubmit;
export {
    inquirySubmit,
    inquiryUpdateStorage,
    inquiryPostSubmission,
    inquiryModalRequestToBook,
    buildInquiryModel,
    inquiryPostApplink
};
